/**
 * WritingStyleCard Component
 *
 * T073: Added screen reader announcements for status changes
 */

import { memo, useCallback, useEffect, useRef } from 'react';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, CheckCircle2, ExternalLink, Trash2, Eye } from 'lucide-react';
import { IWritingStyleCardProps } from '@/@types/writingStyle';

const WritingStyleCard = ({ style, onDelete, onViewAnalysis }: IWritingStyleCardProps) => {
  const handleDelete = useCallback(() => onDelete(style._id), [style._id, onDelete]);
  const handleViewAnalysis = useCallback(() => {
    if (onViewAnalysis) {
      onViewAnalysis(style._id);
    }
  }, [style._id, onViewAnalysis]);

  // T073: Track previous status for screen reader announcements
  const prevStatusRef = useRef(style.status);

  // T073: Screen reader announcement when status changes
  useEffect(() => {
    if (prevStatusRef.current !== style.status) {
      // Create an aria-live region announcement
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';

      let message = '';
      switch (style.status) {
        case 'completed':
          message = `Writing style "${style.name}" analysis completed. Ready for use.`;
          break;
        case 'failed':
          message = `Writing style "${style.name}" analysis failed. ${style.errorMessage || 'Please try again.'}`;
          break;
        case 'analyzing':
          message = `Writing style "${style.name}" is being analyzed.`;
          break;
      }

      announcement.textContent = message;
      document.body.appendChild(announcement);

      // Remove announcement after screen reader has time to read it
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);

      prevStatusRef.current = style.status;
    }
  }, [style.status, style.name, style.errorMessage]);

  const getStatusDisplay = () => {
    switch (style.status) {
      case 'analyzing':
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Analyzing...
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="default" className="flex items-center gap-1 bg-green-600">
            <CheckCircle2 className="h-3 w-3" />
            Ready
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Failed
          </Badge>
        );
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold truncate">{style.name}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Created {formatDate(style.createdAt)}
            </p>
          </div>
          {getStatusDisplay()}
        </div>
      </CardHeader>

      <CardContent className="pb-3">
        <div className="space-y-2">
          {style.sampleUrl && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ExternalLink className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{new URL(style.sampleUrl).hostname}</span>
            </div>
          )}

          {style.status === 'analyzing' && (
            <p className="text-sm text-muted-foreground">
              AI is analyzing the writing style...
            </p>
          )}

          {style.status === 'failed' && (
            <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
              {style.errorMessage || 'Analysis failed. Please try again.'}
            </div>
          )}

          {style.status === 'completed' && (
            <p className="text-sm text-green-600">
              Style ready for use in post generation
            </p>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex gap-2 pt-3 border-t">
        {/* T065: View Analysis button - only show for completed styles */}
        {style.status === 'completed' && onViewAnalysis && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewAnalysis}
          >
            <Eye className="h-4 w-4 mr-1" />
            View Analysis
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={style.status === 'analyzing'}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Delete
        </Button>
      </CardFooter>
    </Card>
  );
};

export default memo(WritingStyleCard);
